import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { GlobeIcon, PaperclipIcon, ArrowRight, Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { type ProviderType } from "@/ai/providers";
import { getRecommendedModel, DEFAULT_MODELS } from "@/ai/models";
import { useAIProvider } from "@/hooks/use-ai-provider";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { marked } from "marked";
import { getRAGContext } from "@/lib/rag-service";
import { getCompanyKnowledgeData } from "@/lib/knowledge-base-service";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import {
  PromptInput,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachments,
  PromptInputAttachment,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputModelSelect,
  PromptInputModelSelectContent,
  PromptInputModelSelectItem,
  PromptInputModelSelectTrigger,
  PromptInputModelSelectValue,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
  type PromptInputMessage,
} from '@/components/ai-elements/prompt-input';
import { Response } from '@/components/ai-elements/response';
import { motion, AnimatePresence } from 'framer-motion';
import { nanoid } from 'nanoid';
import { z } from "zod";
import { streamAIText, parsePartialJson } from "@/lib/ai-text";

const AI_AGENT_SUGGESTIONS = [
  "Create a data privacy policy",
  "What policies do we currently have?",
  "Show policies needing review",
  "Generate an HR policy template",
  "What's our compliance status?",
  "Create a remote work policy",
  "What policies are pending approval?",
  "Generate an IT security policy",
  "Which policies are about to expire?",
  "Create a code of conduct",
];

const openaiModels = [
  { id: 'gpt-4.1', name: 'GPT-4.1' },
  { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini' },
  { id: 'gpt-4.1-nano', name: 'GPT-4.1 Nano' },
  { id: 'gpt-4o', name: 'GPT-4o' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
];

const geminiModels = [
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
  { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite' },
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash' },
  { id: 'gemini-2.0-flash-lite', name: 'Gemini 2.0 Flash Lite' },
  { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro' },
  { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash' },
];

interface PendingDraft {
  title: string;
  content: string;
  department: string;
  description?: string;
}

interface AIMessage {
  key: string;
  from: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  attachments?: Array<{
    type: 'image' | 'pdf';
    url: string;
    mediaType?: string;
    filename?: string;
  }>;
  pendingDraft?: PendingDraft;
}

// Three animated dots shown while waiting for first streaming token
const ThinkingDots = () => (
  <div className="flex items-center gap-1 py-1.5">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-zinc-400 dark:bg-zinc-500 animate-bounce"
        style={{ animationDelay: `${i * 160}ms`, animationDuration: '960ms' }}
      />
    ))}
  </div>
);

// Attachment button component that can use the hook
const AttachmentButton = () => {
  const attachments = usePromptInputAttachments();

  return (
    <PromptInputButton
      onClick={() => attachments.openFileDialog()}
      title="Attach files"
    >
      <PaperclipIcon className="size-4" />
    </PromptInputButton>
  );
};

const PolybotAIAgent = () => {
  const { currentProvider, availableProviders, changeProvider, isProviderAvailable } = useAIProvider();
  const [model, setModel] = useState<string>(() => getRecommendedModel(currentProvider, 'CHAT'));
  const { toast } = useToast();
  const { profile } = useAuth();
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [botName, setBotName] = useState(() => localStorage.getItem('bot_name') || 'AI Agent');
  const [policyContextCache, setPolicyContextCache] = useState<string>("");
  const [lastPolicyRefresh, setLastPolicyRefresh] = useState<Date>(new Date());
  const [refreshingPolicies, setRefreshingPolicies] = useState(false);
  const [useComplianceSearch, setUseComplianceSearch] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const recommendedModel = getRecommendedModel(currentProvider, 'CHAT');
    setModel(recommendedModel);
  }, [currentProvider]);

  const models = currentProvider === 'gemini' ? geminiModels : openaiModels;

  useEffect(() => {
    if (profile?.company_id) {
      fetchNonApprovedPolicyMetadata(true);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    const handleStorageChange = () => {
      const newBotName = localStorage.getItem('bot_name') || 'AI Agent';
      if (newBotName !== botName) {
        setBotName(newBotName);
      }
    };

    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [botName]);

  const handleRefreshPolicies = async () => {
    setRefreshingPolicies(true);
    try {
      await fetchNonApprovedPolicyMetadata(true);
      toast({
        title: "Policies Refreshed",
        description: "AI now has the latest policy information.",
      });
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: "Failed to refresh policy information.",
        variant: "destructive",
      });
    } finally {
      setRefreshingPolicies(false);
    }
  };

  const getCompanyKnowledge = async (): Promise<string> => {
    if (profile?.company_id) {
      try {
        const data = await getCompanyKnowledgeData(profile.company_id);
        if (data && Object.keys(data).length > 0) {
          const lines: string[] = ['## Company Profile\n'];
          if (data.companyName) lines.push(`**Company:** ${data.companyName}`);
          if (data.industry) lines.push(`**Industry:** ${data.industry}`);
          if (data.companySize) lines.push(`**Size:** ${data.companySize}`);
          if (data.foundedYear) lines.push(`**Founded:** ${data.foundedYear}`);
          if (data.whatWeDo) lines.push(`\n**What we do:** ${data.whatWeDo}`);
          if (data.keyProducts) lines.push(`\n**Key products/services:** ${data.keyProducts}`);
          if (data.mission) lines.push(`\n**Mission:** ${data.mission}`);
          if (data.values) lines.push(`\n**Core values:** ${data.values}`);
          if (data.cultureNotes) lines.push(`\n**Culture:** ${data.cultureNotes}`);
          if (data.departments) lines.push(`\n**Departments:** ${data.departments}`);
          if (data.complianceAreas) lines.push(`\n**Compliance areas:** ${data.complianceAreas}`);
          if (data.geography) lines.push(`\n**Geographic presence:** ${data.geography}`);
          if (data.acronyms) lines.push(`\n**Terminology:** ${data.acronyms}`);
          if (data.keyPeople) lines.push(`\n**Key people/roles:** ${data.keyPeople}`);
          if (data.anythingElse) lines.push(`\n**Additional context:** ${data.anythingElse}`);
          const supabaseKnowledge = lines.join('\n');
          if (supabaseKnowledge.trim() !== '## Company Profile') return supabaseKnowledge;
        }
      } catch {
        // fall through
      }
    }
    return localStorage.getItem("company_knowledge") || "";
  };

  const fetchNonApprovedPolicyMetadata = async (forceRefresh = false): Promise<string> => {
    if (!profile?.company_id) return "";

    if (!forceRefresh && policyContextCache && (new Date().getTime() - lastPolicyRefresh.getTime()) < 60000) {
      return policyContextCache;
    }

    try {
      const { data: policiesData, error: policiesError } = await supabase
        .from('policies')
        .select(`
          id, title, description, department, status, version,
          effective_date, next_renewal_date, created_by,
          assigned_reviewers, assigned_approvers, created_at, updated_at
        `)
        .eq('company_id', profile.company_id)
        .neq('status', 'approved')
        .order('created_at', { ascending: false });

      if (policiesError) throw policiesError;

      if (!policiesData || policiesData.length === 0) {
        return "No non-approved policies found in the system.";
      }

      const userIds = new Set<string>();
      policiesData.forEach(policy => {
        if (policy.created_by) userIds.add(policy.created_by);
        if (policy.assigned_reviewers) {
          policy.assigned_reviewers.forEach((id: string) => userIds.add(id));
        }
        if (policy.assigned_approvers) {
          policy.assigned_approvers.forEach((id: string) => userIds.add(id));
        }
      });

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, designation')
        .in('id', Array.from(userIds));

      if (profilesError) throw profilesError;

      const profileMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      const policyContext = policiesData.map(policy => {
        const creator = profileMap.get(policy.created_by);
        const reviewers = policy.assigned_reviewers?.map((id: string) => profileMap.get(id)?.name).filter(Boolean) || [];
        const approvers = policy.assigned_approvers?.map((id: string) => profileMap.get(id)?.name).filter(Boolean) || [];

        const departmentMap: Record<string, string> = {
          'hr': 'Human Resources',
          'it': 'Information Technology',
          'finance': 'Finance',
          'operations': 'Operations',
          'compliance': 'Compliance',
          'security': 'Security',
          'general': 'General'
        };

        return `
- **${policy.title}** (ID: ${policy.id})
  - Department: ${departmentMap[policy.department] || policy.department || 'Not specified'}
  - Status: ${policy.status}
  - Version: ${policy.version}
  - Description: ${policy.description || 'No description available'}
  - Created by: ${creator?.name || 'Unknown'} ${creator?.designation ? `(${creator.designation})` : ''}
  - Effective Date: ${policy.effective_date || 'Not set'}
  - Next Renewal: ${policy.next_renewal_date || 'Not set'}
  - Reviewers: ${reviewers.length > 0 ? reviewers.join(', ') : 'None assigned'}
  - Approvers: ${approvers.length > 0 ? approvers.join(', ') : 'None assigned'}
  - Last Updated: ${new Date(policy.updated_at).toLocaleDateString()}`;
      }).join('\n');

      const contextData = `
## Non-Approved Policies (Metadata Only)

The company currently has ${policiesData.length} non-approved policies:

${policyContext}

## Policy Status Guide
- **draft**: Policy is being written/edited, not yet submitted for review
- **under_review**: Policy is being reviewed by assigned reviewers
- **pending_approval**: Policy has been reviewed and is awaiting final approval
- **rejected**: Policy was rejected and needs revision

Note: Approved policies are available through semantic search and will be retrieved based on your query.
`;

      setPolicyContextCache(contextData);
      setLastPolicyRefresh(new Date());
      return contextData;
    } catch (error) {
      console.error('Error fetching non-approved policy metadata:', error);
      return "Error loading non-approved policy information.";
    }
  };

  const convertMarkdownToHtml = (markdown: string): string => {
    try {
      marked.setOptions({ breaks: true, gfm: true });
      const result = marked(markdown);
      return typeof result === 'string' ? result : markdown;
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      return markdown;
    }
  };

  const saveAsDraft = async (
    title: string,
    content: string,
    messageKey: string,
    department = 'General',
    description?: string,
  ) => {
    try {
      if (!profile?.company_id) throw new Error('Company ID not found');

      const htmlContent = convertMarkdownToHtml(content);

      const { error } = await supabase
        .from('policies')
        .insert({
          title,
          content: htmlContent,
          description: description ?? null,
          department,
          status: 'draft',
          company_id: profile.company_id,
          created_by: profile.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      const successMessage: AIMessage = {
        key: nanoid(),
        from: 'assistant',
        content: `**Draft saved — "${title}"**\n\nFind it under Draft Policies in the Policy Creator. Department set to **${department}**.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMessage]);

      await fetchNonApprovedPolicyMetadata(true);

      toast({
        title: "Policy created",
        description: `"${title}" saved as draft.`,
      });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to save policy';
      console.error('Failed to save policy:', error);
      toast({
        title: "Error",
        description: msg,
        variant: "destructive"
      });
    }
  };

  const confirmDraft = async (messageKey: string, draft: PendingDraft) => {
    // Remove pendingDraft immediately so buttons disappear
    setMessages(prev => prev.map(msg =>
      msg.key === messageKey ? { ...msg, pendingDraft: undefined } : msg
    ));
    await saveAsDraft(draft.title, draft.content, messageKey, draft.department, draft.description);
  };

  const discardDraft = (messageKey: string) => {
    setMessages(prev => prev.map(msg =>
      msg.key === messageKey ? { ...msg, pendingDraft: undefined } : msg
    ));
    toast({ title: "Draft discarded", description: "The policy was not saved." });
  };


  const sendMessage = async (content: string, attachments: AIMessage['attachments'] = []) => {
    if (!content.trim() && (!attachments || attachments.length === 0)) return;
    if (isLoading) return;

    const userMessage: AIMessage = {
      key: nanoid(),
      from: 'user',
      content,
      timestamp: new Date(),
      attachments
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setIsThinking(true);
    setError(null);

    try {
      const assistantMessage: AIMessage = {
        key: nanoid(),
        from: 'assistant',
        content: '',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      const modelName = model || getRecommendedModel(currentProvider, 'CHAT');

      const [companyKnowledge, nonApprovedMetadata] = await Promise.all([
        getCompanyKnowledge(),
        fetchNonApprovedPolicyMetadata(),
      ]);

      let ragContext = '';
      try {
        if (profile?.company_id) {
          console.log('Fetching RAG context for query:', content);
          ragContext = await getRAGContext(content, profile.company_id, 5);
          if (ragContext) {
            console.log('RAG context retrieved, length:', ragContext.length);
          }
        }
      } catch (error) {
        console.error('Error fetching RAG context:', error);
      }

      const systemPrompt = `You are an expert corporate policy AI agent for ${companyKnowledge ? 'this specific company' : 'a regulated company'}. ${useComplianceSearch ? 'You have access to internet search capabilities to provide up-to-date compliance information.' : ''}

## INTENT CLASSIFICATION — Read this before every response

Classify the user's intent and respond accordingly:

- **QUESTION / STATUS CHECK** (e.g. "what policies do we have?", "what does GDPR say about X?", "which policies are pending?") → Respond conversationally and concisely. Do NOT produce a full policy document. Do NOT call \`createDraftPolicy\`.
- **EXPLICIT POLICY REQUEST** (user says "create", "draft", "write", or "generate a policy") → Write the complete policy document in Markdown, then call the \`createDraftPolicy\` tool to save it automatically.
- **GAP ANALYSIS / RECOMMENDATIONS** (e.g. "what policies are we missing?", "suggest improvements") → Respond with a structured bullet-point analysis. Do NOT call \`createDraftPolicy\`.
- **DOCUMENT ANALYSIS** (user attaches a file) → Analyse and summarise the content. Do NOT call \`createDraftPolicy\` unless explicitly asked.

**Never call \`createDraftPolicy\` unless the user has explicitly asked to create or draft a policy.**

---

## Markdown Formatting Rules

- Use **#** for the main policy title, **##** for sections, **###** for subsections
- Use **-** for bullets, **1.** for numbered procedures
- Use **bold** for emphasis — never use bold as a substitute for headers
- Write formally and professionally for corporate policy documents

---
${ragContext ? `
## Retrieved Knowledge (Semantic Search Results)

The following was retrieved based on the user's query. Use it as your primary source of truth.

### Company Policies (Approved)
${ragContext.includes('Knowledge Base') ? ragContext.split('Knowledge Base')[0] : ragContext}
${ragContext.includes('Knowledge Base') ? `
### Regulatory & Compliance Documents (Knowledge Base)
${ragContext.split('Knowledge Base')[1] ?? ''}` : ''}

---
` : ''}
${companyKnowledge ? `## Company Profile

${companyKnowledge}

---
` : ''}
${nonApprovedMetadata}

---

## Capabilities

- **Status queries**: Answer questions about policy workflow states
- **Compliance assessment**: Identify compliant vs. gap areas using retrieved policy content
- **Policy drafting**: Write complete, structured policies when explicitly asked — saves automatically as draft
- **Gap analysis**: Identify missing policies or coverage gaps
- **Document analysis**: Review uploaded PDFs or documents and answer questions about them`;

      abortRef.current = new AbortController();

      const multimodalMessages = [...messages, userMessage].map(msg => {
        if (msg.from === 'user' && msg.attachments && msg.attachments.length > 0) {
          const contentArray: unknown[] = [];

          if (msg.content.trim()) {
            contentArray.push({ type: 'text', text: msg.content });
          }

          msg.attachments.forEach(att => {
            if (att.type === 'image') {
              contentArray.push({ type: 'image', image: att.url });
            } else if (att.type === 'pdf') {
              contentArray.push({
                type: 'file',
                data: att.url,
                mediaType: att.mediaType || 'application/pdf',
                filename: att.filename
              });
            }
          });

          return { role: 'user' as const, content: contentArray };
        } else {
          return {
            role: msg.from === 'user' ? ('user' as const) : ('assistant' as const),
            content: msg.content
          };
        }
      });

      setIsThinking(false);

      const createDraftPolicyTool = {
        description: "Save a newly written policy as a draft in the company policy system. Only call this after you have written the complete policy content in your response.",
        inputSchema: z.object({
          title: z.string().describe('Policy title'),
          content: z.string().describe('Full policy content in Markdown'),
          department: z.string().describe('One of: Legal, Human Resources, Finance, Technology & Data, Risk & Compliance, General'),
          description: z.string().describe('One-sentence summary of what this policy covers'),
        }),
      };

      let hasContent = false;
      let toolCalled = false;
      await streamAIText({
        provider: currentProvider,
        model: modelName,
        messages: multimodalMessages,
        system: systemPrompt,
        temperature: 0.3,
        maxOutputTokens: 8000,
        tools: { createDraftPolicy: createDraftPolicyTool },
        onChunk: (delta) => {
          hasContent = true;
          setMessages(prev => prev.map(msg =>
            msg.key === assistantMessage.key
              ? { ...msg, content: msg.content + delta }
              : msg
          ));
        },
        onToolInputDelta: async (toolName, accumulated) => {
          if (toolName === 'createDraftPolicy') {
            toolCalled = true;
            const { value } = await parsePartialJson(accumulated);
            const partialContent = (value as Record<string, unknown> | undefined)?.content;
            if (typeof partialContent === 'string' && partialContent.length > 0) {
              setMessages(prev => prev.map(msg =>
                msg.key === assistantMessage.key
                  ? { ...msg, content: partialContent }
                  : msg
              ));
            }
          }
        },
        onToolCall: async (toolName, input) => {
          if (toolName === 'createDraftPolicy') {
            const { title, content, department, description } = input as {
              title: string;
              content: string;
              department: string;
              description: string;
            };
            // Show confirmation instead of auto-saving
            setMessages(prev => prev.map(msg =>
              msg.key === assistantMessage.key
                ? { ...msg, pendingDraft: { title, content, department, description } }
                : msg
            ));
          }
        },
      });

      // Only show the error fallback if neither text nor a tool call arrived
      if (!hasContent && !toolCalled) {
        setMessages(prev => prev.map(msg =>
          msg.key === assistantMessage.key
            ? { ...msg, content: "The server appears to be unavailable. Please try again or contact info@manageaze.com" }
            : msg
        ));
      }

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error in sendMessage:', err);
      setError(msg);
      setIsThinking(false);
    } finally {
      abortRef.current = null;
      setIsLoading(false);
    }
  };

  const handleSubmit = async (message: PromptInputMessage) => {
    const hasText = Boolean(message.text);
    const hasAttachments = Boolean(message.files?.length);
    if (!(hasText || hasAttachments)) return;

    if (message.files && message.files.length > 0) {
      try {
        const attachments = await Promise.all(
          message.files.map(async (file) => {
            const isImage = file.mediaType?.startsWith('image/');
            const isPdf = file.mediaType === 'application/pdf' || file.filename?.toLowerCase().endsWith('.pdf');
            const isText = file.mediaType?.startsWith('text/') || file.filename?.toLowerCase().match(/\.txt$/);
            const isWordDoc = file.mediaType?.includes('wordprocessingml') || file.filename?.toLowerCase().match(/\.(doc|docx)$/);

            if (isWordDoc) {
              toast({
                title: 'Word documents not supported',
                description: 'Please convert to PDF or copy the text into a .txt file.',
                variant: 'destructive'
              });
              return null;
            }

            if (!isImage && !isPdf && !isText) {
              toast({
                title: 'Unsupported file type',
                description: 'Only images, PDFs, and .txt files are supported',
                variant: 'destructive'
              });
              return null;
            }

            let dataUrl = file.url;
            if (file.url?.startsWith('blob:')) {
              const response = await fetch(file.url);
              const blob = await response.blob();
              dataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
            }

            return {
              type: isImage ? 'image' : isPdf ? 'pdf' : 'text',
              url: dataUrl,
              mediaType: file.mediaType || (isPdf ? 'application/pdf' : isText ? 'text/plain' : file.mediaType),
              filename: file.filename
            };
          })
        );

        const validAttachments = attachments.filter(Boolean) as AIMessage['attachments'];
        sendMessage(message.text || 'Attached files', validAttachments);
      } catch (error) {
        console.error('Error processing files:', error);
        toast({
          title: 'Error',
          description: 'Failed to process attached files',
          variant: 'destructive'
        });
      }
    } else {
      sendMessage(message.text || '');
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setIsLoading(false);
  }, []);

  return (
    <div className="flex flex-col h-full overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
      {/* ── Messages ─────────────────────────────────────────────── */}
      <Conversation className="flex-1 overflow-y-auto">
        <ConversationContent className="min-h-full p-0">
          {messages.length === 0 ? (
            /* ── Empty state ── */
            <div className="relative flex h-full min-h-[420px] flex-col items-center justify-center px-6 py-12 select-none overflow-hidden">
              {/* Ambient glow */}
              <div className="pointer-events-none absolute inset-0 flex items-start justify-center overflow-hidden pt-8">
                <div className="h-[360px] w-[600px] rounded-full bg-violet-500/[0.07] blur-[100px] dark:bg-violet-500/[0.12]" />
              </div>

              <div className="relative z-10 mb-8 flex flex-col items-center text-center">
                {/* Eyebrow */}
                <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-violet-200/80 bg-violet-50 px-3 py-1 text-[10px] font-semibold tracking-widest uppercase text-violet-600 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-400">
                  Policy Intelligence
                </div>
                {/* Bot name */}
                <h1 className="mb-2 text-[28px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
                  {botName}
                </h1>
                <p className="max-w-xs text-[13.5px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                  Ask about policies, check compliance status, or draft a new document.
                </p>
              </div>

              {/* Suggestion grid */}
              <div className="relative z-10 grid w-full max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                {AI_AGENT_SUGGESTIONS.slice(0, 6).map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="group flex items-start justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left text-[13px] font-medium text-zinc-700 transition-all duration-200 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 active:scale-[0.98] dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-300 dark:hover:border-violet-700 dark:hover:bg-violet-950/30 dark:hover:text-violet-300"
                  >
                    <span className="leading-snug">{suggestion}</span>
                    <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-violet-500 dark:text-zinc-600 dark:group-hover:text-violet-400" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* ── Message list ── */
            <div className="flex flex-col py-4">
              <AnimatePresence initial={false}>
                {messages.map((message) => (
                  <motion.div
                    key={message.key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    {message.from === 'user' ? (
                      /* User bubble — right aligned */
                      <div className="group flex justify-end px-4 pb-1 pt-2">
                        <div className="max-w-[72%] min-w-0">
                          {/* Attachments */}
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mb-2 flex flex-wrap justify-end gap-2">
                              {message.attachments.map((att, idx) => (
                                <div key={idx}>
                                  {att.type === 'image' ? (
                                    <img
                                      src={att.url}
                                      alt={att.filename || 'attachment'}
                                      className="max-w-[220px] rounded-xl border border-zinc-200 dark:border-zinc-700"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-100 px-3 py-2 text-xs text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
                                      <PaperclipIcon className="h-3 w-3 shrink-0" />
                                      <span className="truncate max-w-[160px]">{att.filename || 'Document'}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Message bubble */}
                          <div className="rounded-2xl rounded-tr-sm bg-zinc-100 px-4 py-3 text-[13.5px] leading-relaxed text-zinc-800 dark:bg-zinc-800/70 dark:text-zinc-100">
                            <Response>{message.content}</Response>
                          </div>
                          {/* Hover timestamp */}
                          <div className="mt-0.5 text-right">
                            <span className="text-[10px] tabular-nums text-zinc-400 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Assistant message — full width with avatar */
                      <div className="group px-4 pb-2 pt-3">
                        <div className="flex gap-3">
                          {/* Square avatar */}
                          <div className="mt-0.5 shrink-0">
                            <div className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] bg-violet-600 text-[10px] font-bold text-white shadow-sm shadow-violet-600/25">
                              {botName.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          {/* Content */}
                          <div className="min-w-0 flex-1">
                            {/* Name + timestamp */}
                            <div className="mb-1.5 flex items-baseline gap-2">
                              <span className="text-[12px] font-semibold text-zinc-900 dark:text-zinc-50">
                                {botName}
                              </span>
                              <span className="text-[10px] tabular-nums text-zinc-400 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            {/* Thinking dots or content */}
                            {(isThinking || isLoading) && message.content === '' ? (
                              <ThinkingDots />
                            ) : (
                              <div className="prose prose-sm dark:prose-invert max-w-none text-[13.5px] prose-p:leading-relaxed prose-p:my-1.5 prose-headings:font-semibold prose-headings:tracking-tight prose-pre:text-xs prose-code:text-xs">
                                <Response>{message.content}</Response>
                              </div>
                            )}
                            {/* Draft confirmation */}
                            {message.pendingDraft && (
                              <div className="mt-3 flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 dark:border-violet-800/50 dark:bg-violet-950/30">
                                <div className="min-w-0 flex-1">
                                  <p className="text-[12px] font-medium text-violet-900 dark:text-violet-200">
                                    Save <span className="font-semibold">"{message.pendingDraft.title}"</span> as a draft?
                                  </p>
                                  <p className="text-[11px] text-violet-600 dark:text-violet-400">
                                    {message.pendingDraft.department} · Draft
                                  </p>
                                </div>
                                <button
                                  onClick={() => confirmDraft(message.key, message.pendingDraft!)}
                                  className="flex items-center gap-1 rounded-lg bg-violet-600 px-2.5 py-1.5 text-[11px] font-medium text-white transition-colors hover:bg-violet-700"
                                >
                                  <Check className="h-3 w-3" />
                                  Save
                                </button>
                                <button
                                  onClick={() => discardDraft(message.key)}
                                  className="flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-zinc-600 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                                >
                                  <X className="h-3 w-3" />
                                  Discard
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      {/* ── Input ──────────────────────────────────────────────────── */}
      <div className="shrink-0 px-3 pb-3 pt-1.5">
        <div className="ai-input-container overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] dark:border-zinc-700 dark:bg-zinc-900 dark:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.4)]">
          <PromptInput
            globalDrop
            multiple
            accept="image/*,application/pdf,.pdf,text/plain,.txt"
            maxFileSize={25 * 1024 * 1024}
            onError={(err) => {
              toast({ title: 'File upload error', description: err.message, variant: 'destructive' });
            }}
            onSubmit={handleSubmit}
            className="rounded-none border-0 bg-transparent shadow-none"
          >
            <PromptInputBody className="px-1">
              <PromptInputAttachments>
                {(attachment) => <PromptInputAttachment data={attachment} />}
              </PromptInputAttachments>
              <PromptInputTextarea
                placeholder="Ask about a policy, or describe one to draft…"
                className="min-h-[40px] bg-transparent focus-visible:ring-0 focus:ring-0 text-[13.5px] placeholder:text-zinc-400 dark:placeholder:text-zinc-600"
              />
            </PromptInputBody>

            <PromptInputFooter className="!border-t !border-zinc-200 dark:!border-zinc-700/60 !flex !flex-row !items-center !justify-between !gap-2 !px-2 !py-1.5">
              <PromptInputTools className="!flex !flex-row !items-center !gap-1">
                <AttachmentButton />

                <PromptInputButton
                  onClick={() => setUseComplianceSearch(!useComplianceSearch)}
                  variant={useComplianceSearch ? 'default' : 'ghost'}
                  className={useComplianceSearch
                    ? 'h-7 bg-violet-600 hover:bg-violet-500 text-white text-xs px-2.5 gap-1.5'
                    : 'h-7 text-zinc-500 hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-300 text-xs px-2.5 gap-1.5'
                  }
                  title={useComplianceSearch ? 'Web search on' : 'Enable web search'}
                >
                  <GlobeIcon size={13} />
                  <span className="hidden sm:inline">Compliance Search</span>
                </PromptInputButton>

                {availableProviders.length > 1 && (
                  <Select
                    value={currentProvider}
                    onValueChange={(value) => changeProvider(value as ProviderType)}
                  >
                    <SelectTrigger className="h-7 w-[100px] border-0 bg-transparent text-xs text-zinc-500 hover:text-zinc-700 focus:ring-0 dark:text-zinc-500 dark:hover:text-zinc-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProviders.map((provider) => (
                        <SelectItem key={provider} value={provider} className="text-xs">
                          {provider === 'openai' ? 'OpenAI' : 'Gemini'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <PromptInputModelSelect onValueChange={setModel} value={model}>
                  <PromptInputModelSelectTrigger className="h-7 border-0 bg-transparent text-xs text-zinc-500 hover:text-zinc-700 focus:ring-0 dark:text-zinc-500 dark:hover:text-zinc-300">
                    <PromptInputModelSelectValue />
                  </PromptInputModelSelectTrigger>
                  <PromptInputModelSelectContent>
                    {models.map((m) => (
                      <PromptInputModelSelectItem key={m.id} value={m.id} className="text-xs">
                        {m.name}
                      </PromptInputModelSelectItem>
                    ))}
                  </PromptInputModelSelectContent>
                </PromptInputModelSelect>
              </PromptInputTools>

              <PromptInputSubmit
                status={isLoading ? 'streaming' : 'ready'}
                className="!h-8 !w-8 !min-w-0 !p-0 rounded-[10px] bg-violet-600 hover:bg-violet-500 active:scale-[0.96] active:bg-violet-700 text-white transition-[transform,color,background-color] duration-150 shrink-0 shadow-sm shadow-violet-600/30"
              />
            </PromptInputFooter>
          </PromptInput>
        </div>
      </div>
    </div>
  );
};

export default PolybotAIAgent;
